import { useState } from 'react';
import { Trans } from '../i18n/Trans';
import { ActionButton } from './ActionButton';

export interface QuoteLine {
  sourceQuotes?: Array<{
    source: 'jlc' | 'taobao'; status: 'priced' | 'reference' | 'unavailable';
    title?: string; url?: string; price?: number; currency?: string;
    unitPrice?: number; packQuantity?: number; eligible: boolean; reason: string;
  }>;
  row: number;
  mpn: string;
  qty: number;
  desc?: string;
  brand?: string;
  matchedMpn?: string;
  manufacturer?: string;
  seller?: string;
  currency?: string;
  unitCost?: number;
  unitPrice?: number;
  amount?: number;
  stock?: number;
  moq?: number;
  note?: string;
  /** 供应商原始报价与币种（外币时表格里给个小 icon 点开看） */
  srcPrice?: number;
  srcCurrency?: string;
  fxRate?: number;
  /** 平台上的相近型号：**只提示，不替代**，要用户自己确认 */
  altMpn?: string;
}

export interface QuoteResult {
  createdAt: string;
  /** 这次估价在后端的历史 id；下单只回传它，不回传价格 */
  historyId?: number;
  /** 免登录下单的领取凭据（historyId 是自增数字，光靠它谁都能猜） */
  claimToken?: string;
  markupPercent?: number;
  currency: string;
  lines: QuoteLine[];
  total: number;
  priced: number;
  count: number;
}

/** 单价：位数统一成 4 位小数，列才对得齐（原来 0.0163 和 486 混排看着歪） */
const unit = (v?: number) => (typeof v === 'number' ? v.toFixed(4) : '');
/** 金额与合计：钱一律两位小数 */
const money = (v?: number) => (typeof v === 'number' ? v.toFixed(2) : '');
/** 数量/库存：千分位，同样右对齐 */
const int = (v?: number) => (typeof v === 'number' ? v.toLocaleString('en-US') : '');

/**
 * 报价单表格。估价结果与订单详情共用一份 —— 两处的列、对齐、位数必须一模一样，
 * 否则同一张单在两个页面看着不像同一张单。
 * 对齐纪律：文字列左对齐、数字列右对齐且表头也右对齐（th.num），数字用等宽数字字形。
 */
export function QuoteTable(
  { quote, canSeeCost, fxMeta }:
  { quote: QuoteResult; canSeeCost: boolean; fxMeta?: { day: string; source: string } },
) {
  // 点开的是哪一行的汇率详情（hover 不承载信息：触屏上没有 hover）
  const [fxOpen, setFxOpen] = useState<number | null>(null);
  // 内部视角多两列（来源 + 成本单价），合计行与详情行的跨列数要跟着变
  const cols = 4 + (canSeeCost ? 2 : 0);   // 合计行要跨过前面这些列
  const allCols = 8 + (canSeeCost ? 2 : 0); // 汇率详情那一行要横跨整张表
  return (
    <div className="table-wrap">
      <table className="grid quote-grid">
        <thead>
          <tr>
            <th className="num">#</th>
            <th>{Trans.t('col.mpn')}</th>
            <th>{Trans.t('col.brand')}</th>
            <th className="num">{Trans.t('col.qty')}</th>
            {/* 「来源」是渠道信息，只给内部（有 quote.cost 的）看；客户端这一列整个不出现 */}
            {canSeeCost && <th>{Trans.t('col.source')}</th>}
            {canSeeCost && <th className="num">{Trans.t('col.cost')}</th>}
            <th className="num">{Trans.t('col.price')}</th>
            <th className="num">{Trans.t('col.amount')}</th>
            <th className="num">{Trans.t('col.stock')}</th>
            <th>{Trans.t('col.note')}</th>
          </tr>
        </thead>
        <tbody>
          {quote.lines.map((l) => [
            <tr key={l.row} className={typeof l.unitPrice === 'number' ? '' : 'row-miss'}>
              <td className="num dim">{l.row}</td>
              <td className="mpn">
                {l.mpn}
                {/* 只有精确同料号才会有 matchedMpn；altMpn 是"平台上的相近型号"，
                    用"?"而不是"→"，别让人以为我们已经替代了 */}
                {l.altMpn && <div className="sub alt">相近型号 {l.altMpn}？待确认</div>}
              </td>
              <td>{l.manufacturer ?? l.brand ?? ''}</td>
              <td className="num">{int(l.qty)}</td>
              {canSeeCost && <td>{l.seller ? <span className="pill">{
                l.seller === 'jlc' || l.seller === 'taobao' ? Trans.t(`source.${l.seller}`) : l.seller
              }</span> : ''}</td>}
              {canSeeCost && <td className="num">{unit(l.unitCost)}</td>}
              <td className="num">
                {unit(l.unitPrice)}
                {/* 外币折算过来的，给个小 icon：点开看平台原价、用的汇率、汇率日期 */}
                {l.srcCurrency && l.srcCurrency !== 'USD' && (
                  <button
                    className="fx-icon"
                    title={Trans.t('fx.iconTip')}
                    aria-label={Trans.t('fx.title')}
                    onClick={() => setFxOpen(fxOpen === l.row ? null : l.row)}
                  >⇄</button>
                )}
              </td>
              <td className="num strong">{money(l.amount)}</td>
              <td className="num dim">{int(l.stock)}</td>
              <td className="note">
                {l.note ?? ''}
                {canSeeCost && !!l.sourceQuotes?.length && (
                  <details className="source-quotes">
                    <summary>{Trans.t('source.details')}</summary>
                    {[...l.sourceQuotes].sort((a, b) => a.source === b.source
                      ? Number(b.eligible) - Number(a.eligible) : a.source === 'jlc' ? -1 : 1).map((q, index) => (
                      <div key={index} className="source-quote">
                        <b>{Trans.t(`source.${q.source}`)}</b>{' · '}
                        {Trans.t(q.status === 'unavailable' ? 'source.unavailable' : q.eligible ? 'source.eligible' : 'source.reference')}
                        {typeof q.price === 'number' && <div>{q.price} {q.currency}
                          {q.packQuantity ? ` / ${q.packQuantity} ${Trans.t('source.pack')}` : ''}
                          {typeof q.unitPrice === 'number' && ` → ${q.unitPrice.toFixed(6)} ${q.currency} / pc`}
                        </div>}
                        {q.title && <div>{q.url ? <a href={q.url} target="_blank" rel="noopener noreferrer">{q.title}</a> : q.title}</div>}
                        <div className="dim">{q.reason}</div>
                      </div>
                    ))}
                  </details>
                )}
              </td>
            </tr>,
            fxOpen === l.row ? (
              <tr key={`${l.row}-fx`} className="fx-row">
                <td colSpan={allCols}>
                  <div className="fx-detail">
                    <span className="fx-detail-title">{Trans.t('fx.title')}</span>
                    <span><i>{Trans.t('fx.src')}</i><b>{l.srcPrice} {l.srcCurrency}</b></span>
                    <span><i>{Trans.t('fx.rate')}</i><b>1 {l.srcCurrency} = {l.fxRate} USD</b></span>
                    <span><i>{Trans.t('fx.usd')}</i><b>{unit(l.unitPrice)} USD</b></span>
                    {fxMeta?.day && <span><i>{Trans.t('fx.day')}</i><b>{fxMeta.day}</b></span>}
                    {fxMeta?.source && <span><i>{Trans.t('fx.source')}</i><b>{fxMeta.source}</b></span>}
                    {/* 复制也要有文字反馈：剪贴板这种"看不见的动作"最需要一句"已复制" */}
                    <ActionButton
                      className="btn ghost tiny"
                      inline
                      doneLabel={Trans.t('common.copied')}
                      onRun={async () => {
                        const text = [
                          l.mpn, l.matchedMpn ?? '', `${l.qty}`, canSeeCost ? (l.seller ?? '') : '',
                          `${l.srcPrice} ${l.srcCurrency}`, `1 ${l.srcCurrency}=${l.fxRate} USD`,
                          `${l.unitPrice} USD`,
                        ].filter(Boolean).join('\t');
                        await navigator.clipboard.writeText(text);
                      }}
                    >{Trans.t('fx.copy')}</ActionButton>
                  </div>
                </td>
              </tr>
            ) : null,
          ])}
          {/* 汇率详情：展开成一整行，别做浮层 —— .table-wrap 有 overflow-x，浮层会被裁掉 */}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={cols} />
            <td className="num">{Trans.t('quote.sumTotal')}</td>
            <td className="num strong">{money(quote.total)}</td>
            <td className="num">{quote.currency}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
